// A bottom sheet for the mobile layout: slides up over a dimmed page, closes with the × button, a tap
// outside, Escape, or by dragging it down. Stays mounted while it slides away.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { ChevronRight, X } from '@/components/ui/icons';

export function Sheet({
  open,
  onClose,
  title,
  children,
  className,
  full,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  className?: string;
  /** take (almost) the whole screen, like Create and Settings */
  full?: boolean;
}) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const [drag, setDrag] = useState(0);
  const start = useRef<number | null>(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const raf = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(raf);
    }
    setShown(false);
    const t = setTimeout(() => setMounted(false), 260);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!mounted) return null;
  return createPortal(
    <div className={clsx('m-sheet-layer', { shown })} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className={clsx('m-sheet', className, { full, dragging: start.current !== null })}
        role="dialog"
        aria-modal="true"
        style={drag ? { transform: `translateY(${drag}px)` } : undefined}
      >
        <div
          className="m-sheet-head"
          onTouchStart={(e) => (start.current = e.touches[0].clientY)}
          onTouchMove={(e) => start.current !== null && setDrag(Math.max(0, e.touches[0].clientY - start.current))}
          onTouchEnd={() => {
            if (drag > 80) onClose();
            start.current = null;
            setDrag(0);
          }}
        >
          <span className="m-sheet-grip" />
          {title && <div className="m-sheet-title">{title}</div>}
          <button type="button" className="m-sheet-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="m-sheet-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

/** A tappable row in a sheet: icon, label, optional value on the right and a chevron. */
export function SheetRow({
  icon,
  label,
  value,
  chevron,
  danger,
  onClick,
  right,
}: {
  icon?: ReactNode;
  label: ReactNode;
  value?: ReactNode;
  chevron?: boolean;
  danger?: boolean;
  onClick?: () => void;
  right?: ReactNode;
}) {
  return (
    <button type="button" className={clsx('m-row', { danger })} onClick={onClick} disabled={!onClick && !right}>
      {icon && <span className="m-row-icon">{icon}</span>}
      <span className="m-row-label truncate">{label}</span>
      {value !== undefined && <span className="m-row-value truncate">{value}</span>}
      {right}
      {chevron && <ChevronRight size={18} className="m-row-chevron" />}
    </button>
  );
}

export function SheetSection({ title, children }: { title?: ReactNode; children: ReactNode }) {
  return (
    <section className="m-section">
      {title && <div className="m-section-title">{title}</div>}
      {children}
    </section>
  );
}
